"use client"

import type { ReactNode } from "react"
import { useTranslations } from "next-intl"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { BloodMembershipComparison } from "@/components/ui/blood-membership-comparison"
import { BiomarkerDiseaseExplorer } from "@/components/ui/biomarker-disease-explorer"
import {
  BiomarkerHeroSection,
  BiomarkerDetailCardSection,
  BiomarkerFactsSection,
  BiomarkerGapSection,
  BiomarkerRisksSection,
  BiomarkerActionSection,
  BiomarkerClosingSection,
  LinkedCopy,
} from "@/components/ui/biomarker-landing"
import { BiomarkerStepsWalkthrough } from "@/components/ui/biomarker-steps-walkthrough"
import { ADD_ON_PRODUCT_IDS } from "@/lib/booking"
import { PlanSiteClose } from "@/app/(frontend)/[country]/[locale]/_components/membership-first/pricing/pricing-v5-client"
import {
  BloodHeroPromises,
  BloodPlanHeroPrice,
  HeroSaveButton,
  MriPlanScope,
} from "@/app/(frontend)/[country]/[locale]/_components/membership-first/pricing/pricing-v5-mri-cards"
import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { PlanBloodBlock } from "@/app/(frontend)/[country]/[locale]/_components/membership-first/pricing/pricing-v5-blood-landing"
import type { PricingV5Data } from "@/app/(frontend)/[country]/[locale]/_components/membership-first/pricing/pricing-v5-page-data"
import { lpaConfig } from "./lpa-config"

export default function LpaClient({
  testimonials,
  faq,
  priceFrom,
  planData,
}: {
  /** Streamed CMS sections — async server components in `<Suspense>`,
   *  provided by the page so a slow CMS delays only the section. */
  testimonials: ReactNode
  faq: ReactNode
  priceFrom?: string
  /** The membership-first site's plan prices: the page then sells the test
   *  on top of the Baseline plan, as the /tests blood pages (01.10). */
  planData?: PricingV5Data
}) {
  const lp = useLocalePath()
  const t = useTranslations("go")
  const plan = !!planData
  const config = lpaConfig(
    t,
    lp("/what-we-detect"),
    lp("/tests/advanced-blood-test"),
    priceFrom,
    plan
  )
  const { biomarkerPanel } = config
  const track = `test-${config.slug}`

  return (
    <MriPlanScope data={planData}>
      <BiomarkerHeroSection
        config={config}
        planPrice={
          plan
            ? (dark) => (
                <BloodPlanHeroPrice
                  dark={dark}
                  track={track}
                  addonKey="addonLpa"
                />
              )
            : undefined
        }
        planPromises={
          plan
            ? (dark) =>
                dark ? (
                  <BloodHeroPromises
                    tone="light"
                    className="w-full text-left"
                  />
                ) : (
                  <BloodHeroPromises className="mt-8 gap-6 [&>li]:w-[140px] [&>li]:flex-none" />
                )
            : undefined
        }
        planSave={
          plan
            ? (dark) => <HeroSaveButton itemKey="addonLpa" dark={dark} />
            : undefined
        }
        planCtaClassName={
          plan
            ? cn(
                buttonVariants({ size: "lg" }),
                "min-w-[280px] bg-neutral-950 hover:bg-neutral-800"
              )
            : undefined
        }
      />
      <BiomarkerDetailCardSection config={config} simple={plan} />
      {/* The plan site: the offer third, right after what you get, as
          on every test page (Eugene, 01.10: it sat too far down). */}
      {plan && (
        <>
          <PlanBloodBlock addonKey="addonLpa" track={track} />
          {testimonials}
        </>
      )}
      <BiomarkerFactsSection config={config} />
      <BiomarkerRisksSection config={config} />
      <BiomarkerGapSection config={config} />
      <BiomarkerActionSection config={config} />
      <BiomarkerStepsWalkthrough config={config} plain={plan} />
      {/* Biomarker panel (FH-style) — makes "part of the blood test" concrete */}
      <section className="scroll-mt-24 bg-neutral-50 py-12 md:py-16">
        <BiomarkerDiseaseExplorer
          packages={["blood", "addon-single"]}
          // Payload doc id for Lipoprotein (a) (locale-invariant). Do NOT use
          // the slug — it is a localized field and can differ per locale.
          addonIds={["188"]}
          tabsAllowed={["biomarkers"]}
          diseaseContentEnabled={false}
          isBloodTest
          eyebrow={biomarkerPanel.eyebrow}
          biomarkersTab={{
            heading: biomarkerPanel.heading,
            subheading: (
              <LinkedCopy
                text={biomarkerPanel.subheading}
                phrase="Advanced blood test"
                href={config.advancedBloodTestHref}
              />
            ),
            cta: {
              label: biomarkerPanel.ctaLabel,
              href: lp("/biomarkers"),
              variant: "text",
            },
          }}
        />
      </section>
      {/* Real social proof (CMS testimonials); on the plan site under the
          plan block, as on the /tests blood pages. */}
      {!plan && testimonials}
      {/* Pricing off the plan site: the add-on vs membership card. The plan
          site has its offer third, above. */}
      {!plan && (
        <div className="py-8 md:py-12">
          <BloodMembershipComparison />
        </div>
      )}
      {faq}
      {plan ? (
        <PlanSiteClose productId={ADD_ON_PRODUCT_IDS.addonLpa} />
      ) : (
        <BiomarkerClosingSection config={config} />
      )}
    </MriPlanScope>
  )
}
